import { BaseEventAdapter } from './eventAdapter';
import type { TicketmasterEvent, EventItem, Place } from '../../types/shared/event';
import type { SupportedLanguages } from '../../i18n';
import { ticketmasterTranslations } from '../ticketmaster/translations';
import { ticketmasterClient } from '../ticketmaster/client';
import { logger } from '../logger';
import { calculateAllTravelTimes } from '../../utils/travelTimeCalculator';
import { supabaseClient } from '../supabase';
import { SUPABASE_TABLES } from '../supabase/tables';

class TicketmasterEventAdapter extends BaseEventAdapter<any> {
  protected source = 'ticketmaster' as const;
  private missingTerms: Set<string> = new Set();
  private locale: string = 'fr';

  public formatEvent(rawEvent: any): TicketmasterEvent | null {
    try {
      // Initial validation
      if (!rawEvent?.name || !rawEvent?.dates?.start?.localDate) {
        logger.warn('Missing required fields in Ticketmaster event', {
          category: 'API',
          data: { eventId: rawEvent?.id }
        });
        return null;
      }

      // Extract essential data
      const venue = rawEvent._embedded?.venues?.[0];
      if (!venue) {
        logger.warn('Missing venue information', {
          category: 'API',
          data: { eventId: rawEvent.id }
        });
        return null;
      }

      const location = venue.location;
      if (!location?.latitude || !location?.longitude) {
        logger.warn('Missing location coordinates', {
          category: 'API',
          data: { eventId: rawEvent.id, venueId: venue.id }
        });
        return null;
      }

      // Create group key
      const groupKey = this.createGroupKey({
        name: rawEvent.name,
        description: rawEvent.description || rawEvent.info || '',
        place: venue.name
      });

      // Generate showId from group key
      const showId = this.generateShowIdFromKey(groupKey);

      // Get category and type from classifications
      const category = rawEvent.classifications?.[0]?.segment?.name || 'Autre';
      const type = rawEvent.classifications?.[0]?.genre?.name || '';

      // Add terms to check for translations
      this.addTermToCheck(category);
      if (type) {
        this.addTermToCheck(type);
      }

      logger.debug('Catégorie et type de l\'événement Ticketmaster', {
        category: 'Translation',
        data: { 
          eventId: rawEvent.id,
          eventName: rawEvent.name,
          category,
          type,
          locale: this.locale, // Using the locale from config
          colonne_recherche: this.locale, // We'll search in the locale column of trad_cat_type
          rawClassifications: rawEvent.classifications
        }
      });

      logger.debug('Event grouping information', {
        category: 'API',
        data: {
          eventId: rawEvent.id,
          groupKey,
          showId,
          venue: venue.name
        }
      });

      // Log price information for debugging
      if (rawEvent.priceRanges && rawEvent.priceRanges.length > 0) {
        logger.debug('Price information found', {
          category: 'API',
          data: {
            eventId: rawEvent.id,
            priceRanges: rawEvent.priceRanges,
            firstPrice: rawEvent.priceRanges[0]
          }
        });
      } else {
        logger.debug('No price information found', {
          category: 'API',
          data: { eventId: rawEvent.id }
        });
      }

      // Calculate distance in km
      const distance = Number(venue.distance) || 0;
      
      // Calculate travel times based on distance
      const { walkTime, bikeTime, driveTime } = calculateAllTravelTimes(distance);

      // Extract duration and calculate end time
      let duration = undefined;
      let endTime = undefined;
      
      // Check if there's duration info in the event
      if (rawEvent.dates?.start?.localTime && rawEvent.dates?.end?.localTime) {
        const startTime = rawEvent.dates.start.localTime;
        const endTimeFromApi = rawEvent.dates.end.localTime;
        
        // Calculate duration in minutes
        const [startHours, startMinutes] = startTime.split(':').map(Number);
        const [endHours, endMinutes] = endTimeFromApi.split(':').map(Number);
        
        const startTotalMinutes = startHours * 60 + startMinutes;
        const endTotalMinutes = endHours * 60 + endMinutes;
        
        // Handle cases where the event ends after midnight
        duration = endTotalMinutes >= startTotalMinutes 
          ? endTotalMinutes - startTotalMinutes
          : (24 * 60) - startTotalMinutes + endTotalMinutes;
        
        // Format end time as HH:MM
        endTime = endTimeFromApi.substring(0, 5);
        
        logger.debug('Event duration information', {
          category: 'API',
          data: {
            eventId: rawEvent.id,
            startTime,
            endTime,
            durationMinutes: duration
          }
        });
      }

      // Format event
      const formattedEvent: TicketmasterEvent = {
        id: rawEvent.id,
        source: this.source,
        eventId: rawEvent.id,
        showId,
        name: rawEvent.name,
        descriptions: {},
        description: rawEvent.description || rawEvent.info || '',
        fulladress: this.formatAddress(venue),
        telephone: venue.boxOfficeInfo?.phoneNumberDetail || '',
        url: rawEvent.url || '',
        itinerary: venue.url || '',
        category: category,
        type: type,
        image: this.getBestImage(rawEvent.images),
        latitude: Number(location.latitude),
        longitude: Number(location.longitude),
        distance: distance,
        booking: rawEvent.url || '',
        price: this.formatPrice(rawEvent.priceRanges?.[0]),
        artiste: rawEvent._embedded?.attractions?.[0]?.name || '',
        place: venue.name,
        date: rawEvent.dates.start.localDate,
        hour: (rawEvent.dates.start.localTime || '00:00:00').substring(0, 5),
        duration: duration,
        end_time: endTime,
        // Add calculated travel times
        timewalk: walkTime.toString(),
        timebike: bikeTime.toString(),
        timedrive: driveTime.toString()
      };

      return formattedEvent;

    } catch (error) {
      logger.error('Error formatting Ticketmaster event', {
        category: 'API',
        data: { error, eventId: rawEvent?.id }
      });
      return null;
    }
  }

  private addTermToCheck(term: string): void {
    if (term && term.trim() !== '') {
      this.missingTerms.add(term);
    }
  }

  public async checkMissingTermsInTradCatType(): Promise<string[]> {
    const missingTermsArray = Array.from(this.missingTerms);
    const confirmedMissingTerms: string[] = [];

    if (missingTermsArray.length === 0) {
      return [];
    }

    logger.debug('Checking terms in trad_cat_type', {
      category: 'Translation',
      data: { 
        termsToCheck: missingTermsArray,
        count: missingTermsArray.length,
        locale: this.locale
      }
    });

    try {
      // Process each term individually to check if it exists in the column matching the locale
      for (const term of missingTermsArray) {
        // Query trad_cat_type for the term in the column matching the locale
        const { data, error } = await supabaseClient
          .from(SUPABASE_TABLES.CATEGORY_TRANSLATIONS.name)
          .select('*')
          .eq(this.locale, term)
          .maybeSingle();

        if (error) {
          logger.error(`Error checking term "${term}" in trad_cat_type`, {
            category: 'Translation',
            data: { error, term, locale: this.locale }
          });
          continue;
        }

        // If the term doesn't exist in the column matching the locale, add it to confirmedMissingTerms
        if (!data) {
          confirmedMissingTerms.push(term);
          logger.debug(`Term "${term}" not found in trad_cat_type column "${this.locale}"`, {
            category: 'Translation',
            data: { term, locale: this.locale }
          });
        } else {
          logger.debug(`Term "${term}" found in trad_cat_type column "${this.locale}"`, {
            category: 'Translation',
            data: { term, locale: this.locale, record: data }
          });
        }
      }

      if (confirmedMissingTerms.length > 0) {
        logger.debug('Missing terms in trad_cat_type', {
          category: 'Translation',
          data: { 
            missingTerms: confirmedMissingTerms,
            count: confirmedMissingTerms.length,
            locale: this.locale
          }
        });
      } else {
        logger.debug('All terms found in trad_cat_type', {
          category: 'Translation',
          data: { 
            termsChecked: missingTermsArray.length,
            locale: this.locale
          }
        });
      }

      // Clear the set after checking
      this.missingTerms.clear();

      return confirmedMissingTerms;
    } catch (error) {
      logger.error('Error checking terms in trad_cat_type', {
        category: 'Translation',
        data: { error, termsToCheck: missingTermsArray }
      });
      
      // Clear the set on error
      this.missingTerms.clear();
      
      return [];
    }
  }

  public setLocale(locale: string): void {
    this.locale = locale;
  }

  private createGroupKey(params: {
    name: string;
    description: string;
    place: string;
  }): string {
    // Normalize values
    const normalizedName = params.name.toLowerCase().trim();
    const normalizedDescription = params.description.toLowerCase().trim();
    const normalizedPlace = params.place.toLowerCase().trim();

    // Create unique key
    return `${normalizedName}|${normalizedDescription}|${normalizedPlace}`;
  }

  private generateShowIdFromKey(key: string): string {
    try {
      // Create a hash of the key
      let hash = 0;
      for (let i = 0; i < key.length; i++) {
        const char = key.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }

      // Create a prefixed and unique ID
      return `tm_${Math.abs(hash).toString(36)}`;
    } catch (error) {
      logger.error('Error generating showId', {
        category: 'API',
        data: { error, key }
      });
      // Fallback to timestamp-based ID
      return `tm_${Date.now().toString(36)}`;
    }
  }

  private getBestImage(images: any[] = []): string {
    if (!images?.length) return '';

    try {
      // Prefer 3:2 ratio images, then 16:9
      const sortedImages = images
        .filter(img => img.ratio === '3_2' || img.ratio === '16_9')
        .sort((a, b) => {
          // Prefer 3:2 ratio
          if (a.ratio === '3_2' && b.ratio !== '3_2') return -1;
          if (b.ratio === '3_2' && a.ratio !== '3_2') return 1;

          // For same ratio, take highest resolution
          return (b.width * b.height) - (a.width * a.height);
        });

      return sortedImages[0]?.url || images[0]?.url || '';
    } catch (error) {
      logger.error('Error getting best image', {
        category: 'API',
        data: { error }
      });
      return '';
    }
  }

  private formatPrice(priceRange: any): string {
    if (!priceRange) return '';
    
    try {
      // Log the price range for debugging
      logger.debug('Formatting price range', {
        category: 'API',
        data: { priceRange }
      });
      
      // Check if min price exists
      if (priceRange.min === undefined || priceRange.min === null) {
        return '';
      }
      
      // Format the price with currency
      const currency = priceRange.currency || '€';
      const formattedPrice = `${Number(priceRange.min).toFixed(2)} ${currency}`;
      
      return formattedPrice;
    } catch (error) {
      logger.error('Error formatting price', {
        category: 'API',
        data: { error, priceRange }
      });
      return '';
    }
  }

  private formatAddress(venue: any): string {
    if (!venue) return '';
    
    try {
      const addressParts = [
        venue.address?.line1,
        venue.city?.name,
        venue.postalCode
      ].filter(Boolean);

      return addressParts.length > 0 ? addressParts.join(', ') : '';
    } catch (error) {
      logger.error('Error formatting address', {
        category: 'API',
        data: { error, venue }
      });
      return '';
    }
  }

  public async translateDescription(
    description: string,
    targetLang: SupportedLanguages,
    context: { translationTableId: string; itemId: string }
  ): Promise<string> {
    try {
      logger.debug('Starting Ticketmaster translation', {
        category: 'Translation',
        data: { 
          translationTableId: context.translationTableId,
          itemId: context.itemId,
          targetLang,
          descriptionLength: description.length 
        }
      });

      return await ticketmasterTranslations.translateDescription(
        description,
        targetLang,
        context.translationTableId,
        context.itemId
      );
    } catch (error) {
      logger.error('Translation error in TicketmasterEventAdapter', {
        category: 'Translation',
        data: { error, description, targetLang }
      });
      return description;
    }
  }

  public generateShowId(event: EventItem): string {
    if (event.source !== 'ticketmaster') {
      throw new Error('Invalid event source');
    }
    return event.showId || `tm_${Date.now().toString(36)}`;
  }

  public getFavoriteId(event: EventItem): string {
    if (event.source !== 'ticketmaster') {
      throw new Error('Invalid event source');
    }
    return `ticketmaster_${event.eventId}`;
  }

  public async getEvents(config: any, searchParams: any): Promise<TicketmasterEvent[]> {
    logger.debug('Ticketmaster adapter getEvents called', {
      category: 'API',
      data: { 
        config: {
          radius: config.radius,
          size: config.size,
          locale: config.locale,
          classificationIds: config.classificationIds
        },
        searchParams: {
          startDateTime: searchParams.startDateTime,
          endDateTime: searchParams.endDateTime
        }
      }
    });

    // Set the locale from config
    this.setLocale(config.locale || 'fr');

    // Get events from Ticketmaster API
    const events = await ticketmasterClient.getEvents({
      ...config,
      ...searchParams
    });

    // Check for missing terms in trad_cat_type
    const missingTerms = await this.checkMissingTermsInTradCatType();
    
    if (missingTerms.length > 0) {
      try {
        // Process each missing term sequentially
        for (const term of missingTerms) {
          logger.debug(`Calling translate-cat-type for term "${term}"`, {
            category: 'Translation',
            data: { term, sourceLanguage: this.locale }
          });
          
          // Get Supabase URL and anon key from environment
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
          
          if (!supabaseUrl || !supabaseAnonKey) {
            logger.error('Missing Supabase URL or anon key', {
              category: 'Translation',
              data: { 
                hasUrl: !!supabaseUrl,
                hasAnonKey: !!supabaseAnonKey
              }
            });
            throw new Error('Missing Supabase URL or anon key');
          }
          
          const response = await fetch(`${supabaseUrl}/functions/v1/translate-cat-type`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`
            },
            body: JSON.stringify({
              term,
              sourceLanguage: this.locale
            })
          });

          if (!response.ok) {
            const errorText = await response.text();
            logger.error(`Error calling translate-cat-type for term "${term}"`, {
              category: 'Translation',
              data: { 
                status: response.status,
                statusText: response.statusText,
                errorText,
                term,
                url: `${supabaseUrl}/functions/v1/translate-cat-type`
              }
            });
            continue; // Continue with next term even if this one fails
          }

          const result = await response.json();
          logger.debug(`Translated term "${term}" successfully`, {
            category: 'Translation',
            data: { result }
          });
        }
      } catch (error) {
        logger.error('Error calling edge function to translate terms', {
          category: 'Translation',
          data: { error, missingTerms }
        });
      }
    }

    return events;
  }

  public async translateCategoryOrType(
    term: string,
    sourceLanguage: SupportedLanguages
  ): Promise<Record<string, string>> {
    try {
      // Check if the term already exists in trad_cat_type
      const { data: existingTranslations, error: checkError } = await supabaseClient
        .from('trad_cat_type')
        .select('*')
        .eq(sourceLanguage, term)
        .single();

      // Log detailed search in trad_cat_type
      logger.debug('Checking term in trad_cat_type', {
        category: 'Translation',
        data: { 
          term,
          sourceLanguage,
          colonne_recherche: sourceLanguage,
          existingTranslations: existingTranslations ? 
            Object.fromEntries(
              Object.entries(existingTranslations)
                .filter(([key]) => ['fr', 'en', 'es', 'it', 'pt', 'de', 'nl', 'ja', 'zh', 'ru'].includes(key))
            ) : null,
          error: checkError
        }
      });

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw checkError;
      }

      // If the term exists, return the existing translations
      if (existingTranslations) {
        logger.debug('Term found in trad_cat_type', {
          category: 'Translation',
          data: { 
            term,
            id: existingTranslations.id,
            translations: Object.fromEntries(
              Object.entries(existingTranslations)
                .filter(([key]) => ['fr', 'en', 'es', 'it', 'pt', 'de', 'nl', 'ja', 'zh', 'ru'].includes(key))
            )
          }
        });

        // Extract translations for all supported languages
        const translations: Record<string, string> = {};
        ['fr', 'en', 'es', 'it', 'pt', 'de', 'nl', 'ja', 'zh', 'ru'].forEach(lang => {
          translations[lang] = existingTranslations[lang] || term;
        });

        return translations;
      }

      // If the term doesn't exist, call the edge function to translate it
      logger.debug('Term not found in trad_cat_type, calling edge function', {
        category: 'Translation',
        data: { term, sourceLanguage }
      });

      // Get Supabase URL and anon key from environment
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey) {
        logger.error('Missing Supabase URL or anon key', {
          category: 'Translation',
          data: { 
            hasUrl: !!supabaseUrl,
            hasAnonKey: !!supabaseAnonKey
          }
        });
        throw new Error('Missing Supabase URL or anon key');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/translate-cat-type`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          term,
          sourceLanguage
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Edge function error', {
          category: 'Translation',
          data: { 
            status: response.status,
            statusText: response.statusText,
            errorText,
            term,
            url: `${supabaseUrl}/functions/v1/translate-cat-type`
          }
        });
        throw new Error(`Edge function error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        logger.error('Edge function returned error', {
          category: 'Translation',
          data: { 
            error: result.error,
            term
          }
        });
        throw new Error(result.error || 'Unknown error from edge function');
      }

      logger.debug('Translation successful via edge function', {
        category: 'Translation',
        data: { 
          term, 
          sourceLanguage,
          translations: result.translations
        }
      });

      return result.translations || {};

    } catch (error) {
      logger.error('Error translating term', {
        category: 'Translation',
        data: { error, term, sourceLanguage }
      });
      
      // Return a fallback with just the original term
      const fallback: Record<string, string> = {};
      fallback[sourceLanguage] = term;
      return fallback;
    }
  }
}

export const ticketmasterEventAdapter = new TicketmasterEventAdapter();